import React, { useState, useEffect } from "react";
import {
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
  Divider,
  InputLabel,
  Checkbox,
  OutlinedInput,
  ListItemText
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { useAlert } from "../alerts/AlertContext";
import AxiosInstance from "../../services/AxiosInstance";
import { NOTIFICATION_TYPE_URL, URGENCIES_URL, FUNCTIONAL_ROLES_URL, ORG_NOTIFICATION_URL } from "../../Constants/Constants";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";

function NotificationForm({ open, onClose, editData = null, onSuccess, isEditMode = false }) {
  const [formData, setFormData] = useState({
    name: "",
    seq: "",
    active: true,
  });
  const [loading, setLoading] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [notificationTypeId, setNotificationTypeId] = useState(0);
  const [priorities, setPriorities] = useState([]);
  const [priorityId, setPriorityId] = useState(0);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
   const [value, setValue] = React.useState([]);
  const [errors, setErrors] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [options, setOptions] = useState([]);
  const [notification, setNotification] = useState({
    id: "",
    notificationType: {id : ""},
    urgency: {id: ""},
    title: "",
    summary: "",
    audience: "",
    active: "",
    startDate: "",
    endDate: ""    
  });

  const showAlert = useAlert();

  // Initialize form data when dialog opens or editData changes
  useEffect(() => {
    if (open) {
      getNotificationTypes();
      getPriorities();
      getFunctionalRoles();
      if (editData) {
        setFormData({
          title: editData.title || "",
          summary: editData.summary || "",
          active: editData.active !== undefined ? editData.active : true,
        });

        setTitle(editData.title);
        setSummary(editData.summary);
        setNotificationTypeId(editData.notificationType.id);
        setPriorityId(editData.urgency.id);        
        setSelectedIds(editData.audience?.split(",").map(Number));        
        setStartDate(new Date(editData.startDate));
        setEndDate(new Date(editData.endDate));
      } else {
        setFormData({
          name: "",
          seq: "",
          active: true,
        });
      }
      setErrors({});
    }
  }, [open, editData]);

  
  // const options = [
  //   { id: 1, name: "Option A" },
  //   { id: 2, name: "Option B" },
  //   { id: 3, name: "Option C" },
  //   { id: 4, name: "Option D" },
  // ];

  const getNotificationTypes = () => {
    AxiosInstance.get(NOTIFICATION_TYPE_URL).then((response) =>
      setNotificationTypes(response.data)
    );
  };

   const getPriorities = () => {
    AxiosInstance.get(URGENCIES_URL).then((response) =>
      setPriorities(response.data)
    );
  };

  const getFunctionalRoles = () => {
    AxiosInstance.get(FUNCTIONAL_ROLES_URL).then((response) =>
      setOptions(response.data?.map(({ id, name }) => ({ id, name })))
    );
  };

  const handleNotificationTypeChange = (event) => {
    setNotificationTypeId(event.target.value);
  };

  const handlePriorityChange = (event) => {
    setPriorityId(event.target.value);
  };

   const handleTitleChange = (event) => {
    setTitle(event.target.value);
  };

  const handleSummaryChange = (event) => {
    setSummary(event.target.value);
  };
  
  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    // `value` is an array of selected IDs
    setSelectedIds(typeof value === "string" ? value.split(",") : value);
  };


  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.name = "Title is required";
    } else if (formData.title.trim().length < 2) {
      newErrors.name = "Title name must be at least 2 characters";
    } 

    if (!formData.seq.toString().trim()) {
      newErrors.seq = "Sequence is required";
    } else if (isNaN(formData.seq) || parseInt(formData.seq) < 1) {
      newErrors.seq = "Sequence must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // if (!validateForm()) {
    //   return;
    // }

    setLoading(true);

    try {
      const payload = {
        notificationType: {id: notificationTypeId},
        urgency: {id: priorityId},
        title: title,
        summary: summary,
        audience: selectedIds.join(","),
        startDate: startDate,
        endDate: endDate,
        active: formData.active,
        ...(isEditMode && { id: editData.id }),
      };

      await AxiosInstance.post(ORG_NOTIFICATION_URL, payload);

      showAlert(
        `Notification ${isEditMode ? "updated" : "created"} successfully!`,
        "success"
      );

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving notification:", error);
      showAlert(
        `Notification ${isEditMode ? "update" : "creation"} failed!`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 450,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >      
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isEditMode ? (
            <EditIcon color="primary" />
          ) : (
            <AddIcon color="primary" />
          )}
          <Typography variant="h6" component="span">
            {isEditMode ? "Edit Notification" : "Add New Notification"}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          size="small"
          sx={{ color: "grey.500" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <ThemeProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
           <Box
            sx={{
              display: "flex",
              gap: 2,          // space between dropdowns
              width: "100%",   // full parent width
            }}
          >
              <FormControl variant="outlined" fullWidth sx={{ flex: 1 }}>
                    <InputLabel id="demo-simple-select-standard-label">
                      Type
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-standard-label"
                      id="demo-simple-select-standard"
                      value={notificationTypeId}
                      onChange={handleNotificationTypeChange}
                      label="Notification Type"                  
                    >
                      <MenuItem value="0">
                        {" "}
                        <em>Select Type </em>{" "}
                      </MenuItem>
                      {notificationTypes?.map((ntf) => (
                        <MenuItem value={ntf.id}>{ntf.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="outlined" fullWidth sx={{ flex: 1 }}>
                    <InputLabel id="demo-simple-select-standard-label">
                      Priorty
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-standard-label"
                      id="demo-simple-select-standard"
                      value={priorityId}
                      onChange={handlePriorityChange}
                      label="Priority"                  
                    >
                      <MenuItem value="0">
                        {" "}
                        <em>Select Priority</em>{" "}
                      </MenuItem>
                      {priorities?.map((prt) => (
                        <MenuItem value={prt.id}>{prt.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
              </Box>        
             <TextField
                autoFocus
                required
                margin="dense"
                id="outlined-multiline-static"
                name="Title"
                label="Title"
                type="text"
                variant="outlined"
                fullWidth
                value={title}
                onChange={handleTitleChange}
              />
                
            <TextField
                autoFocus
                id="outlined-multiline-static"
                name="summary"
                label="Summary"
                placeholder="Enter Summary"
                fullWidth
                multiline
                rows={4}
                cols={10}
                value={summary}
                onChange={handleSummaryChange}
              />

              <Box
                  sx={{
                    display: "flex",
                    gap: 2,          // space between dropdowns
                    width: "100%",   // full parent width
                  }}
                >
                <FormControl fullWidth sx={{ flex: 1, mt:1 }}>
                  <InputLabel id="multi-select-label">Audience</InputLabel>
                  <Select
                    labelId="multi-select-label"
                    multiple
                    value={selectedIds}
                    onChange={handleChange}
                    input={<OutlinedInput label="Select Options" />}
                    renderValue={(selected) =>
                      options
                        .filter((opt) => selected.includes(opt.id))
                        .map((opt) => opt.name)
                        .join(", ")
                    }
                  >
                    {options.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        <Checkbox checked={selectedIds.includes(opt.id)} />
                        <ListItemText primary={opt.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>     
                <FormControl fullWidth sx={{ flex: 1 }} component="fieldset" disabled={loading}>
                  <FormLabel component="legend">Status</FormLabel>
                  <RadioGroup
                    row
                    name="active"
                    value={formData.active}
                    onChange={(e) =>
                      handleInputChange("active", e.target.value === "true")
                    }
                    sx={{ ml: 1 }}
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label="Active"
                      sx={{ mr: 3 }}
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label="Inactive"
                    />
                  </RadioGroup>
                </FormControl>
                </Box>
             
             <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Publish
              </Typography>

              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <DatePicker
                  label="Start"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />

                <DatePicker
                  label="End"
                  value={endDate}
                  minDate={startDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Box>
            </Box>

          

          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            {isEditMode
              ? "You are editing an existing notification."
              : "You are adding a new notification."}
          </Alert>          
        </Box>
        </LocalizationProvider>
        </ThemeProvider>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{
            borderRadius: 2,
            minWidth: 120,
          }}
        >
          {loading ? "Saving..." : isEditMode ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NotificationForm;
